import { Stack, SvgIcon } from '@mui/material'
import React from 'react'
import { imageTransform } from '@/helpers/images'
import Icon from '@/components/DietaryIconMui/Icon'
import { useTranslation } from 'react-i18next'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import FilterCategory from './FilterCategoryMui'

const canteenMenuFiltersPanelVendorMenuDataFragment = graphql(`
  fragment CanteenMenuFiltersPanel_VendorMenuDataFragment on CCVendorMenu {
    id
    allItems: menuItems(limit: 10) {
      cacheKeys
      pageInfo {
        count
      }
      stats {
        cacheKey
        id
        allergens {
          id
          label
          name
          value
        }
        dietaries {
          id
          label
          name
          value
        }
      }
    }
    vendor {
      id
      companyName
      logo
      images {
        secureUrl
      }
    }
  }
`)

type Props = {
  menuData?: FragmentType<typeof canteenMenuFiltersPanelVendorMenuDataFragment>[]
  loading: boolean
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  onToggleFilterValue: (category: 'vendors' | 'allergens' | 'dietaries', value: string) => void
  onClearFilters: () => void
}

const FiltersPanelMui: React.FC<Props> = ({
  menuData: menuDataProp,
  loading,
  menuFilters,
  onToggleFilterValue,
  onClearFilters,
}) => {
  const { t } = useTranslation()
  const menuData = useFragment(canteenMenuFiltersPanelVendorMenuDataFragment, menuDataProp)

  const handleFilterChange = (category: string, value: string) => {
    if (category === 'vendors' || category === 'dietaries' || category === 'allergens') {
      onToggleFilterValue(category, value)
    }
  }

  const handleClearAll = () => {
    onClearFilters()
  }

  const selectedDietaries = menuFilters.dietaries
  const selectedAllergens = menuFilters.allergens
  const selectedVendors = menuFilters.vendors

  const filterValues =
    menuData?.reduce<Record<string, number>>((values, menu) => {
      const returnValue = { ...values }
      const { allergens, dietaries } = menu.allItems.stats
      const allFilters = [...allergens, ...dietaries]
      return allFilters.reduce((menuFilters, filter) => {
        const existingTotalForFilter = menuFilters[filter.name]

        const newValue =
          typeof existingTotalForFilter === 'number'
            ? existingTotalForFilter + Number(filter.value)
            : filter.value
        return {
          ...menuFilters,
          [filter.name]: newValue || 0,
        }
      }, returnValue)
    }, {}) || {}

  const showLoadingState = loading && !menuData

  return (
    <Stack spacing={1} direction="column">
      <FilterCategory
        loading={showLoadingState}
        categoryTitle={t('cc:filterVendors')}
        filters={
          menuData?.map((menu) => ({
            category: 'vendors',
            selected: selectedVendors?.includes(menu.vendor.id),
            quantity: menu.allItems.pageInfo.count,
            title: menu.vendor.companyName,
            value: menu.vendor.id,
            backgroundImageUrl: imageTransform(menu.vendor.images?.[0]?.secureUrl || '', 300, 70),
          })) || []
        }
        onFilterChange={(category, value) => handleFilterChange(category, value)}
        onClearAll={() => handleClearAll()}
      />
      <FilterCategory
        loading={showLoadingState}
        categoryTitle={t('cc:filterGoal')}
        filters={[
          {
            value: 'vegan',
            category: 'dietaries',
            selected: selectedDietaries?.includes('vegan'),
            quantity: filterValues.vegan || 0,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(4)',
                  top: 18,
                  right: 36,
                }}
              >
                <Icon type="vegan" />
              </SvgIcon>
            ),
            title: t('dietaries:vegan'),
            subtitle: t('dietaries:vegan_description'),
            backgroundColor: '#85a33d',
          },
          {
            value: 'vegetarian',
            category: 'dietaries',
            selected: selectedDietaries?.includes('vegetarian'),
            quantity: filterValues.vegetarian,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(2.7)',
                  top: 3,
                  right: 23,
                }}
              >
                <Icon type="vegetarian" />
              </SvgIcon>
            ),
            title: t('dietaries:vegetarian'),
            subtitle: t('dietaries:vegetarian_description'),
            backgroundColor: '#a2c74b',
          },
          {
            value: 'powerplant',
            category: 'dietaries',
            selected: selectedDietaries?.includes('powerplant'),
            quantity: filterValues.powerplant,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3.2)',
                  right: 16,
                  top: 6,
                }}
              >
                <Icon type="plantPacked" />
              </SvgIcon>
            ),
            title: t('dietaries:powerplant'),
            subtitle: t('dietaries:powerplant_description'),
            backgroundColor: '#58C98F',
          },
          {
            value: 'lowcarb',
            category: 'dietaries',
            selected: selectedDietaries?.includes('lowcarb'),
            quantity: filterValues.lowcarb,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(4)',
                  right: 13,
                  top: -9,
                }}
              >
                <Icon type="lowCarb" />
              </SvgIcon>
            ),
            title: t('dietaries:lowcarb'),
            subtitle: t('dietaries:lowcarb_description'),
            backgroundColor: '#f8a448',
          },
          {
            value: 'highprotein',
            category: 'dietaries',
            selected: selectedDietaries?.includes('highprotein'),
            quantity: filterValues.highprotein,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(4)',
                  right: 13,
                  top: 13,
                }}
              >
                <Icon type="highProtein" />
              </SvgIcon>
            ),
            title: t('dietaries:highprotein'),
            subtitle: t('dietaries:highprotein_description'),
            backgroundColor: '#595a84',
          },
          {
            value: 'isPromoted',
            category: 'dietaries',
            selected: selectedDietaries?.includes('isPromoted'),
            quantity: filterValues.isPromoted || 0,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(2.5)',
                  top: 6,
                  right: 19,
                }}
              >
                <Icon type="expertChoice" />
              </SvgIcon>
            ),
            title: t('dietaries:isPromoted'),
            subtitle: t('dietaries:isPromoted_description'),
            backgroundColor: '#b33e70',
          },
        ]}
        onFilterChange={(category, value) => handleFilterChange(category, value)}
        onClearAll={() => handleClearAll()}
      />
      <FilterCategory
        loading={showLoadingState}
        categoryTitle={t('cc:filterDietary')}
        filters={[
          {
            value: 'halal',
            category: 'dietaries',
            selected: selectedDietaries?.includes('halal'),
            quantity: filterValues.halal,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(2.5)',
                  top: 2,
                  right: 22,
                }}
              >
                <Icon type="halal" />
              </SvgIcon>
            ),
            title: t('dietaries:halal'),
            backgroundColor: '#6c6ea0',
          },
          {
            value: 'refined_sugar',
            category: 'dietaries',
            selected: selectedDietaries?.includes('refined_sugar'),
            quantity: filterValues.refined_sugar,
            backgroundIcon: (
              <SvgIcon sx={{ position: 'relative', transform: 'scale(3)', right: 22 }}>
                <Icon type="noSugar" />
              </SvgIcon>
            ),
            title: t('dietaries:refined_sugar'),
            backgroundColor: '#a369f5',
          },
          {
            value: 'cereals',
            category: 'allergens',
            selected: selectedAllergens?.includes('cereals'),
            quantity: filterValues.cereals,
            backgroundIcon: (
              <SvgIcon sx={{ position: 'relative', transform: 'scale(3)', right: 20 }}>
                <Icon type="noGluten" />
              </SvgIcon>
            ),
            title: t('dietaries:cereals'),
            backgroundColor: '#f6bd60',
          },
          {
            value: 'nuts',
            category: 'allergens',
            selected: selectedAllergens?.includes('nuts'),
            quantity: filterValues.nuts,
            backgroundIcon: (
              <SvgIcon sx={{ position: 'relative', transform: 'scale(3)', right: 22 }}>
                <Icon type="noNuts" />
              </SvgIcon>
            ),
            title: t('dietaries:nuts'),
            backgroundColor: '#f25f5c',
          },
          {
            value: 'milk',
            category: 'allergens',
            selected: selectedAllergens?.includes('milk'),
            quantity: filterValues.milk,
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  right: 16,
                  top: -2,
                }}
              >
                <Icon type="noMilk" />
              </SvgIcon>
            ),
            title: t('dietaries:milk'),
            backgroundColor: '#3aaed8',
          },
        ]}
        onFilterChange={(category, value) => handleFilterChange(category, value)}
        onClearAll={() => handleClearAll()}
      />
      <FilterCategory
        loading={showLoadingState}
        categoryTitle={t('cc:filterAllergens')}
        filters={[
          {
            value: 'crustaceans',
            category: 'allergens',
            selected: selectedAllergens?.includes('crustaceans'),
            quantity: filterValues.crustaceans,
            title: t('allergens:crustaceans'),
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3.2)',
                  right: 17,
                  top: 2,
                }}
              >
                <Icon type="crustaceans" />
              </SvgIcon>
            ),
            backgroundColor: '#FF636D',
          },
          {
            value: 'milk',
            category: 'allergens',
            selected: selectedAllergens?.includes('milk'),
            quantity: filterValues.milk,
            title: t('dietaries:milk'),
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  right: 16,
                  top: -2,
                }}
              >
                <Icon type="noMilk" />
              </SvgIcon>
            ),
            backgroundColor: '#3AAED8',
          },
          {
            value: 'fish',
            category: 'allergens',
            selected: selectedAllergens?.includes('fish'),
            quantity: filterValues.fish,
            title: t('allergens:fish'),
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3.2)',
                  top: 3,
                  right: 20,
                }}
              >
                <Icon type="fish" />
              </SvgIcon>
            ),
            backgroundColor: '#FF636D',
          },
          {
            value: 'peanuts',
            category: 'allergens',
            selected: selectedAllergens?.includes('peanuts'),
            quantity: filterValues.peanuts,
            title: t('allergens:peanuts'),
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  top: 3,
                  right: 13,
                }}
              >
                <Icon type="peanuts" />
              </SvgIcon>
            ),
            backgroundColor: '#FF636D',
          },
          {
            value: 'sesame',
            category: 'allergens',
            selected: selectedAllergens?.includes('sesame'),
            quantity: filterValues.sesame,
            title: t('allergens:sesame'),
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  top: 3,
                  right: 13,
                }}
              >
                <Icon type="sesame" />
              </SvgIcon>
            ),
            backgroundColor: '#FF636D',
          },
          {
            value: 'eggs',
            category: 'allergens',
            selected: selectedAllergens?.includes('eggs'),
            quantity: filterValues.eggs,
            title: t('allergens:eggs'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  top: 2,
                  right: 12,
                }}
              >
                <Icon type="eggs" />
              </SvgIcon>
            ),
          },
          {
            value: 'soybeans',
            category: 'allergens',
            selected: selectedAllergens?.includes('soybeans'),
            quantity: filterValues.soybeans,
            title: t('allergens:soybeans'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  right: 15,
                  top: 3,
                }}
              >
                <Icon type="soybeans" />
              </SvgIcon>
            ),
          },
          {
            value: 'celery',
            category: 'allergens',
            selected: selectedAllergens?.includes('celery'),
            quantity: filterValues.celery,
            title: t('allergens:celery'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  top: 3,
                  right: 20,
                }}
              >
                <Icon type="celery" />
              </SvgIcon>
            ),
          },
          {
            value: 'mustard',
            category: 'allergens',
            selected: selectedAllergens?.includes('mustard'),
            quantity: filterValues.mustard,
            title: t('allergens:mustard'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3)',
                  top: 4,
                  right: 15,
                }}
              >
                <Icon type="mustard" />
              </SvgIcon>
            ),
          },
          {
            value: 'sulphur',
            category: 'allergens',
            selected: selectedAllergens?.includes('sulphur'),
            quantity: filterValues.sulphur,
            title: t('allergens:sulphur'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(2.8)',
                  right: 14,
                  top: 3,
                }}
              >
                <Icon type="sulphur" />
              </SvgIcon>
            ),
          },
          {
            value: 'molluscs',
            category: 'allergens',
            selected: selectedAllergens?.includes('molluscs'),
            quantity: filterValues.molluscs,
            title: t('allergens:molluscs'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(3.5)',
                  right: 17,
                  top: 4,
                }}
              >
                <Icon type="molluscs" />
              </SvgIcon>
            ),
          },
          {
            value: 'lupin',
            category: 'allergens',
            selected: selectedAllergens?.includes('lupin'),
            quantity: filterValues.lupin,
            title: t('allergens:lupin'),
            backgroundColor: '#FF636D',
            backgroundIcon: (
              <SvgIcon
                sx={{
                  position: 'relative',
                  transform: 'scale(6)',
                  right: 20,
                  top: 17,
                }}
              >
                <Icon type="lupin" />
              </SvgIcon>
            ),
          },
        ]}
        onFilterChange={(category, value) => handleFilterChange(category, value)}
        onClearAll={() => handleClearAll()}
      />
    </Stack>
  )
}

export default FiltersPanelMui
